import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../../core/hooks/useIsMobile";
import usePageTitle from "../../core/hooks/UsePageTitle";
import useRevisaoSelecaoStore, {
  DEFAULT_SESSION_LIMIT,
  REVISAO_LIST_DIMENSIONS,
  selectionTokens,
  type RevisaoSelecao,
} from "../../core/store/useRevisaoSelecaoStore";
import type { DayForecast, DeckCount, ReviewFacets } from "../../sdk/@types/Revisao";
import QuestaoService from "../../sdk/services/QuestaoService";
import ReviewService from "../../sdk/services/ReviewService";
import {
  DIA_DIMENSION_LABEL,
  DIA_FICHA_DIMENSION,
  type FichaDeFiltro,
} from "../components/Revisao/barraDeInicioContrato";
import type {
  RevisaoFilterSheetProps,
  RevisaoFilterSheetTab,
  RevisaoMateriaOpcao,
} from "../components/Revisao/filtros/revisaoFilterSheetContrato";
import RevisaoHomeEstreito from "../components/Revisao/home/RevisaoHomeEstreito";
import RevisaoHomeSecoes, {
  type RevisaoHomeSecoesProps,
} from "../components/Revisao/home/RevisaoHomeSecoes";
import RevisaoHomeShell from "../components/Revisao/home/RevisaoHomeShell";
import {
  agendaDePrevisao,
  baralhosDoDiaFixado,
  chaveDaEntrega,
  contagensDoTrilho,
  diaDoIso,
  entregaDaLeitura,
  entregaPraticarDia,
  LEITURA_VAZIA,
  leituraDeRespostas,
  motivoBaralhosDoDiaFixado,
  motivoDiaFixado,
  parametrosDaEntrega,
  type AgendaDia,
  type RevisaoDiaLeitura,
  type RevisaoRailId,
  type RevisaoSessaoEntrega,
  type RevisaoTrilhoContagens,
} from "../components/Revisao/home/revisaoHome";
import DefaultLayout from "../layouts/default/Default.layout";

const DECKS_VAZIOS: string[] = [];

interface MateriaOpcao {
  id: string;
  label: string;
  /** Flashcard count for this subject. 0 for the legacy, imprecise fallback
   *  source (design.md Decisão 6) — never a fabricated number. */
  count: number;
}

interface BaralhoOpcao {
  name: string;
  count: number;
}

function idDeMateria(value: unknown): string | null {
  if (typeof value === "string" && value !== "") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function materiasDaResposta(rows: unknown): MateriaOpcao[] {
  if (!Array.isArray(rows)) return [];
  const materias: MateriaOpcao[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const candidate = row as { id?: unknown; name?: unknown };
    const id = idDeMateria(candidate.id);
    if (id === null || typeof candidate.name !== "string" || candidate.name === "") continue;
    materias.push({ id, label: candidate.name, count: 0 });
  }
  return materias;
}

/** Fallback source has no per-keyword count — `count: 0` reads as "unknown",
 *  same as every other fallback-sourced option (design.md Decisão 6). */
function palavrasDaResposta(rows: unknown): DeckCount[] {
  if (!Array.isArray(rows)) return [];
  const unicas = new Set<string>();
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const description = (row as { description?: unknown }).description;
    if (typeof description !== "string") continue;
    const trimmed = description.trim();
    if (trimmed !== "") unicas.add(trimmed);
  }
  return Array.from(unicas)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ name, count: 0 }));
}

/** Facets as the three dimensions' source (design.md Decisão 6): each
 *  subject/tag/deck the user has at least one card for, already counted,
 *  never windowed to the forecast's seven days. */
function materiasDaFaceta(facets: ReviewFacets): MateriaOpcao[] {
  return facets.subjects.map((subject) => ({
    id: subject.id,
    label: subject.name,
    count: subject.count,
  }));
}

function streakDaResposta(stats: unknown): number | null {
  if (!stats || typeof stats !== "object") return null;
  const streak = (stats as { streak?: unknown }).streak;
  return typeof streak === "number" ? streak : null;
}

function opcoesDeBaralho(
  agenda: readonly AgendaDia[],
  forecast: readonly DayForecast[],
  due: readonly DeckCount[],
): BaralhoOpcao[] {
  const isos = new Set(agenda.map((dia) => dia.iso));
  const soma = new Map<string, number>();
  for (const day of forecast) {
    if (!isos.has(day.day) || !Array.isArray(day.decks)) continue;
    for (const deck of day.decks) {
      if (!deck || typeof deck.name !== "string" || deck.name === "") continue;
      const count = typeof deck.count === "number" ? deck.count : 0;
      soma.set(deck.name, (soma.get(deck.name) ?? 0) + count);
    }
  }

  const duePorNome = new Map<string, number>();
  const nomes: string[] = [];
  const vistos = new Set<string>();
  const incluir = (name: string) => {
    if (name === "" || vistos.has(name)) return;
    vistos.add(name);
    nomes.push(name);
  };

  for (const dia of agenda) {
    for (const name of dia.decks) incluir(name);
  }
  for (const deck of due) {
    if (!deck || typeof deck.name !== "string") continue;
    incluir(deck.name);
    if (typeof deck.count === "number") duePorNome.set(deck.name, deck.count);
  }

  return nomes.map((name) => ({
    name,
    count: soma.has(name) ? (soma.get(name) ?? 0) : (duePorNome.get(name) ?? 0),
  }));
}

function selecaoVazia(): RevisaoSelecao {
  return {
    subjects: [],
    keywords: [],
    decks: [],
    ahead: false,
    limit: DEFAULT_SESSION_LIMIT,
    queueOrder: "due",
  };
}

function copiarSelecao(selecao: RevisaoSelecao): RevisaoSelecao {
  return {
    subjects: [...selecao.subjects],
    keywords: [...selecao.keywords],
    decks: [...selecao.decks],
    ahead: selecao.ahead,
    limit: selecao.limit,
    queueOrder: selecao.queueOrder,
  };
}

function selecaoDasSecoes(secoes: RevisaoHomeSecoesProps): RevisaoSelecao {
  return copiarSelecao({
    subjects: secoes.materiasSelecionadas,
    keywords: secoes.palavrasSelecionadas,
    decks: secoes.baralhosSelecionados,
    ahead: secoes.ahead,
    limit: secoes.limit,
    queueOrder: secoes.queueOrder,
  });
}

function alternar(lista: readonly string[], valor: string): string[] {
  return lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor];
}

function fichasDaSelecao(
  selecao: RevisaoSelecao,
  materias: readonly { id: string; label: string }[],
): FichaDeFiltro[] {
  const nomePorId = new Map(materias.map((materia) => [materia.id, materia.label]));
  return selectionTokens(selecao).map((token) => ({
    dimension: token.dimension,
    dimensionLabel: token.dimensionLabel,
    value: token.value,
    label:
      token.dimension === "subjects" ? (nomePorId.get(token.value) ?? token.value) : token.value,
  }));
}

/**
 * A ficha do dia fixado (no máximo uma — `diaFixado` é um iso só). Nunca lê
 * nem grava a store: removê-la solta `diaFixado`, nunca chama `remove`.
 * Sem o dia correspondente na agenda (ela chega async de `getForecast`,
 * pode não ter populado ainda), cai no iso crú como rótulo em vez de quebrar.
 */
function fichaDoDiaFixado(
  diaFixado: string | null,
  agenda: readonly AgendaDia[],
): FichaDeFiltro | null {
  if (diaFixado === null) return null;
  const dia = agenda.find((item) => item.iso === diaFixado);
  return {
    dimension: DIA_FICHA_DIMENSION,
    dimensionLabel: DIA_DIMENSION_LABEL,
    value: diaFixado,
    label: dia ? `${dia.label} ${dia.date}` : diaFixado,
  };
}

/**
 * The sheet's disabled-state overrides for Matéria, Palavras-chave e
 * Baralhos while a day is fixed (design.md, Decisão 5). Reuses the exact
 * motivo text and baralho-derivation `RevisaoHomeSecoes` already passes to
 * the wide rail's `DimensaoSecao` instances (task `secoes-com-dia-fixado`),
 * so the two viewports describe the identical suspended state in the
 * identical words — not a second, independently-worded copy of the same
 * decision. Empty object when no day is fixed, so spreading it into a
 * `RevisaoFilterSheetProps` literal is a no-op.
 */
function overridesDoDiaFixado(
  agenda: AgendaDia[],
  diaFixado: string | null,
): Pick<
  RevisaoFilterSheetProps,
  "materiaDesabilitada" | "palavrasDesabilitada" | "baralhosDesabilitada" | "baralhosFixados"
> {
  if (diaFixado === null) return {};
  return {
    materiaDesabilitada: { motivo: motivoDiaFixado("matéria") },
    palavrasDesabilitada: { motivo: motivoDiaFixado("palavra-chave") },
    baralhosDesabilitada: { motivo: motivoBaralhosDoDiaFixado() },
    baralhosFixados: baralhosDoDiaFixado(agenda, diaFixado),
  };
}

/** Grava o rascunho na store com os métodos que o trilho largo já usa. */
function aplicarRascunho(
  atual: RevisaoSelecao,
  rascunho: RevisaoSelecao,
  gravar: {
    toggle: (dimension: (typeof REVISAO_LIST_DIMENSIONS)[number], value: string) => void;
    setAhead: (ahead: boolean) => void;
    setLimit: (limit: number) => void;
    setQueueOrder: (queueOrder: RevisaoSelecao["queueOrder"]) => void;
  },
): void {
  for (const dimension of REVISAO_LIST_DIMENSIONS) {
    const naStore = atual[dimension];
    const noRascunho = rascunho[dimension];
    for (const value of noRascunho) {
      if (!naStore.includes(value)) gravar.toggle(dimension, value);
    }
    for (const value of naStore) {
      if (!noRascunho.includes(value)) gravar.toggle(dimension, value);
    }
  }
  if (rascunho.ahead !== atual.ahead) gravar.setAhead(rascunho.ahead);
  if (rascunho.limit !== atual.limit) gravar.setLimit(rascunho.limit);
  if (rascunho.queueOrder !== atual.queueOrder) gravar.setQueueOrder(rascunho.queueOrder);
}

function materiasDaFolha(materias: readonly MateriaOpcao[]): RevisaoMateriaOpcao[] {
  return materias.map((materia) => ({
    value: materia.id,
    label: materia.label,
    count: materia.count,
  }));
}

export interface RevisaoPaginaProps {
  leitura: RevisaoDiaLeitura;
  contagens: RevisaoTrilhoContagens;
  activeId: RevisaoRailId;
  onSelect: (id: RevisaoRailId) => void;
  onIniciar: () => void;
  /** A primeira leitura ainda não chegou. A história não passa isto. */
  aguardando?: boolean;
  fichas: FichaDeFiltro[];
  onRemoverFicha: (ficha: FichaDeFiltro) => void;
  onLimpar: () => void;
  podeLimpar: boolean;
  ahead: boolean;
  onAhead: (ahead: boolean) => void;
  secoes: RevisaoHomeSecoesProps;
  /** Folha já ligada à store. Sem ela, a página estreita guarda o rascunho (história). */
  folha?: RevisaoFilterSheetProps;
  onAbrirFolha?: () => void;
  /** A rota vive dentro da casca. A história não. */
  reservaDaBarraDeAbas?: boolean;
}

function paginaLarga(props: RevisaoPaginaProps): JSX.Element {
  return (
    <RevisaoHomeShell
      leitura={props.leitura}
      contagens={props.contagens}
      activeId={props.activeId}
      onSelect={props.onSelect}
      onIniciar={props.onIniciar}
      aguardando={props.aguardando}
      fichas={props.fichas}
      onRemoverFicha={props.onRemoverFicha}
      onLimpar={props.onLimpar}
      podeLimpar={props.podeLimpar}
      ahead={props.ahead}
      onAhead={props.onAhead}
    >
      <RevisaoHomeSecoes {...props.secoes} />
    </RevisaoHomeShell>
  );
}

function paginaEstreita(
  props: RevisaoPaginaProps,
  estreito: {
    fichas: FichaDeFiltro[];
    baralhosSelecionados: string[];
    ahead: boolean;
    onAbrirFolha: () => void;
    folha: RevisaoFilterSheetProps;
    reservaDaBarraDeAbas?: boolean;
  },
): JSX.Element {
  return (
    <RevisaoHomeEstreito
      leitura={props.leitura}
      aguardando={props.aguardando}
      fichas={estreito.fichas}
      onRemoverFicha={props.onRemoverFicha}
      onLimpar={props.onLimpar}
      podeLimpar={props.podeLimpar}
      ahead={estreito.ahead}
      onAhead={props.onAhead}
      onIniciar={props.onIniciar}
      agenda={props.secoes.agenda}
      diaFixado={props.secoes.diaFixado}
      onFixarDia={props.secoes.onFixarDia}
      onPraticarDia={props.secoes.onPraticarDia}
      baralhos={props.secoes.baralhos}
      baralhosSelecionados={estreito.baralhosSelecionados}
      filtrosEscolhidos={estreito.fichas.length}
      onAbrirFolha={estreito.onAbrirFolha}
      folha={estreito.folha}
      reservaDaBarraDeAbas={estreito.reservaDaBarraDeAbas}
    />
  );
}

/** Composition only. No fetch, no layout chrome. The story renders this. */
export function RevisaoPagina(props: RevisaoPaginaProps): JSX.Element {
  const estreito = useIsMobile();
  const [folhaLocalAberta, setFolhaLocalAberta] = useState(false);
  const [abaLocal, setAbaLocal] = useState<RevisaoFilterSheetTab>("materia");
  const [rascunhoLocal, setRascunhoLocal] = useState<RevisaoSelecao>(() =>
    selecaoDasSecoes(props.secoes),
  );
  const [aplicadaLocal, setAplicadaLocal] = useState<RevisaoSelecao | null>(null);
  const [fichasLocais, setFichasLocais] = useState<FichaDeFiltro[] | null>(null);
  const [baralhosLocais, setBaralhosLocais] = useState<string[] | null>(null);

  if (!estreito) return paginaLarga(props);

  if (props.folha && props.onAbrirFolha) {
    return paginaEstreita(props, {
      fichas: props.fichas,
      baralhosSelecionados: props.secoes.baralhosSelecionados,
      ahead: props.ahead,
      onAbrirFolha: props.onAbrirFolha,
      folha: props.folha,
      reservaDaBarraDeAbas: props.reservaDaBarraDeAbas,
    });
  }

  const fichas = fichasLocais ?? props.fichas;
  const folhaLocal: RevisaoFilterSheetProps = {
    open: folhaLocalAberta,
    onClose: () => setFolhaLocalAberta(false),
    aba: abaLocal,
    onAba: setAbaLocal,
    materias: materiasDaFolha(props.secoes.materias),
    materiasSelecionadas: rascunhoLocal.subjects,
    onToggleMateria: (id) =>
      setRascunhoLocal((prev) => ({ ...prev, subjects: alternar(prev.subjects, id) })),
    palavras: props.secoes.palavras,
    palavrasSelecionadas: rascunhoLocal.keywords,
    onTogglePalavra: (value) =>
      setRascunhoLocal((prev) => ({ ...prev, keywords: alternar(prev.keywords, value) })),
    baralhos: props.secoes.baralhos,
    baralhosSelecionados: rascunhoLocal.decks,
    onToggleBaralho: (name) =>
      setRascunhoLocal((prev) => ({ ...prev, decks: alternar(prev.decks, name) })),
    ahead: rascunhoLocal.ahead,
    onAhead: (ahead) => setRascunhoLocal((prev) => ({ ...prev, ahead })),
    limit: rascunhoLocal.limit,
    onLimit: (limit) => setRascunhoLocal((prev) => ({ ...prev, limit })),
    queueOrder: rascunhoLocal.queueOrder,
    onQueueOrder: (queueOrder) => setRascunhoLocal((prev) => ({ ...prev, queueOrder })),
    resultCount: props.aguardando ? undefined : props.leitura.total,
    onApply: () => {
      const aplicada = copiarSelecao(rascunhoLocal);
      setAplicadaLocal(aplicada);
      setFichasLocais(fichasDaSelecao(aplicada, props.secoes.materias));
      setBaralhosLocais([...aplicada.decks]);
      setFolhaLocalAberta(false);
    },
    onClear: () => setRascunhoLocal(selecaoVazia()),
    ...overridesDoDiaFixado(props.secoes.agenda, props.secoes.diaFixado),
  };

  return paginaEstreita(props, {
    fichas,
    baralhosSelecionados: baralhosLocais ?? props.secoes.baralhosSelecionados,
    ahead: aplicadaLocal?.ahead ?? props.ahead,
    onAbrirFolha: () => {
      setRascunhoLocal(copiarSelecao(aplicadaLocal ?? selecaoDasSecoes(props.secoes)));
      setAbaLocal("materia");
      setFolhaLocalAberta(true);
    },
    folha: folhaLocal,
  });
}

/**
 * Uma leitura só alimenta o herói e a barra de início. `onIniciar` navega
 * com a entrega que essa leitura usou. A agenda não grava `review.weeklySchedule`.
 * `onLimpar` chama `clearAll` e solta o dia fixado.
 * Abaixo de 768px a folha edita um rascunho daqui; aplicar grava na mesma store.
 */
function RevisaoAoVivo(): JSX.Element {
  usePageTitle("Revisão");
  const navigate = useNavigate();
  const hydrated = useRevisaoSelecaoStore((state) => state.hydrated);
  const hydrate = useRevisaoSelecaoStore((state) => state.hydrate);
  const selecao = useRevisaoSelecaoStore((state) => state.selecao);
  const toggle = useRevisaoSelecaoStore((state) => state.toggle);
  const setAhead = useRevisaoSelecaoStore((state) => state.setAhead);
  const setLimit = useRevisaoSelecaoStore((state) => state.setLimit);
  const setQueueOrder = useRevisaoSelecaoStore((state) => state.setQueueOrder);
  const remove = useRevisaoSelecaoStore((state) => state.remove);
  const clearAll = useRevisaoSelecaoStore((state) => state.clearAll);
  const { subjects, keywords, decks, ahead, limit, queueOrder } = selecao;

  const [materiasFallback, setMateriasFallback] = useState<MateriaOpcao[]>([]);
  const [palavrasFallback, setPalavrasFallback] = useState<DeckCount[]>([]);
  const [facets, setFacets] = useState<ReviewFacets | null>(null);
  const [forecast, setForecast] = useState<DayForecast[]>([]);
  const [diaFixado, setDiaFixado] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<RevisaoRailId>("agenda");
  const [leitura, setLeitura] = useState<RevisaoDiaLeitura>(LEITURA_VAZIA);
  const [primeiraLeituraPronta, setPrimeiraLeituraPronta] = useState(false);
  const [folhaAberta, setFolhaAberta] = useState(false);
  const [aba, setAba] = useState<RevisaoFilterSheetTab>("materia");
  const [rascunho, setRascunho] = useState<RevisaoSelecao>(selecaoVazia);
  const [contagemDaFolha, setContagemDaFolha] = useState<number | undefined>(undefined);
  /** Chave do rascunho a que `contagemDaFolha` pertence. Outra chave não reaproveita o número. */
  const [chaveContada, setChaveContada] = useState<string | null>(null);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    let cancelado = false;
    QuestaoService.getSubjectsBySubscriber()
      .then((res) => {
        if (!cancelado) setMateriasFallback(materiasDaResposta(res));
      })
      .catch(() => {
        if (!cancelado) setMateriasFallback([]);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!hydrated) return undefined;
    let cancelado = false;
    const timer = setTimeout(() => {
      const pedido =
        subjects.length === 0
          ? QuestaoService.buscarTagsDasPalavrasChaves()
          : QuestaoService.getTagsByContextSubscriber({ subjectIds: subjects });
      pedido
        .then((res) => {
          if (!cancelado) setPalavrasFallback(palavrasDaResposta(res));
        })
        .catch(() => {
          if (!cancelado) setPalavrasFallback([]);
        });
    }, 300);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [hydrated, subjects]);

  useEffect(() => {
    let cancelado = false;
    ReviewService.getForecast(7)
      .then((days) => {
        if (!cancelado) setForecast(Array.isArray(days) ? days : []);
      })
      .catch(() => {
        if (!cancelado) setForecast([]);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  // Facets are the three dimensions' source; while the route errors or
  // doesn't exist, `facets` stays null and every dimension keeps reading its
  // fallback source untouched (design.md Decisão 6). Fetched once — not
  // per-selection, unlike the fallback tags effect above, because the
  // inventory doesn't depend on the draft.
  useEffect(() => {
    let cancelado = false;
    ReviewService.getFacets()
      .then((result) => {
        if (!cancelado) setFacets(result);
      })
      .catch(() => {
        if (!cancelado) setFacets(null);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  const materias = useMemo(
    () => (facets ? materiasDaFaceta(facets) : materiasFallback),
    [facets, materiasFallback],
  );
  const palavras = useMemo(
    () => (facets ? facets.tags : palavrasFallback),
    [facets, palavrasFallback],
  );

  const agenda = useMemo(() => agendaDePrevisao(forecast), [forecast]);
  const diaDecks = useMemo(() => {
    if (!diaFixado) return DECKS_VAZIOS;
    return agenda.find((dia) => dia.iso === diaFixado)?.decks ?? DECKS_VAZIOS;
  }, [agenda, diaFixado]);
  const entrega = useMemo(
    () =>
      entregaDaLeitura({
        diaFixado,
        diaDecks,
        subjectIds: subjects,
        keywords,
        decks,
        ahead,
        limit,
      }),
    [ahead, decks, diaDecks, diaFixado, keywords, limit, subjects],
  );
  const chave = chaveDaEntrega(entrega);
  const entregaRef = useRef<RevisaoSessaoEntrega>(entrega);
  entregaRef.current = entrega;
  // A entrega que a leitura na tela usou. Iniciar leva esta, não a seleção
  // que ainda está no ar enquanto o próximo getDue não voltou.
  const entregaMostrada = useRef<RevisaoSessaoEntrega>(entrega);

  useEffect(() => {
    if (!hydrated) return undefined;
    let cancelado = false;
    const pedido = entregaRef.current;
    Promise.allSettled([
      ReviewService.getDue(parametrosDaEntrega(pedido)),
      ReviewService.getStats(),
    ]).then(([dueResult, statsResult]) => {
      if (cancelado) return;
      const due = dueResult.status === "fulfilled" ? dueResult.value : null;
      const streak = statsResult.status === "fulfilled" ? streakDaResposta(statsResult.value) : null;
      entregaMostrada.current = pedido;
      setLeitura(leituraDeRespostas(due, streak));
      setPrimeiraLeituraPronta(true);
    });
    return () => {
      cancelado = true;
    };
  }, [chave, hydrated]);

  const entregaDoRascunho = useMemo(
    () =>
      entregaDaLeitura({
        diaFixado,
        diaDecks,
        subjectIds: rascunho.subjects,
        keywords: rascunho.keywords,
        decks: rascunho.decks,
        ahead: rascunho.ahead,
        limit: rascunho.limit,
      }),
    [diaDecks, diaFixado, rascunho],
  );
  const chaveDoRascunho = chaveDaEntrega(entregaDoRascunho);
  const entregaDoRascunhoRef = useRef(entregaDoRascunho);
  entregaDoRascunhoRef.current = entregaDoRascunho;

  // Mesma entrega da página: o botão reusa `leitura`. Um rascunho diferente
  // pede só o total — não chama `setLeitura`, o herói fica na seleção aplicada.
  useEffect(() => {
    if (!folhaAberta || chaveDoRascunho !== chave) return;
    setChaveContada(chave);
    setContagemDaFolha(primeiraLeituraPronta ? leitura.total : undefined);
  }, [chave, chaveDoRascunho, folhaAberta, leitura.total, primeiraLeituraPronta]);

  useEffect(() => {
    if (!folhaAberta || chaveDoRascunho === chave) return undefined;
    let cancelado = false;
    const pedido = entregaDoRascunhoRef.current;
    ReviewService.getDue(parametrosDaEntrega(pedido))
      .then((due) => {
        if (cancelado) return;
        setChaveContada(chaveDoRascunho);
        setContagemDaFolha(leituraDeRespostas(due, null).total);
      })
      .catch(() => {
        if (cancelado) return;
        setChaveContada(chaveDoRascunho);
        setContagemDaFolha(undefined);
      });
    return () => {
      cancelado = true;
    };
  }, [chave, chaveDoRascunho, folhaAberta]);

  const contagemDoBotao: number | undefined = !folhaAberta
    ? undefined
    : chaveDoRascunho === chave
      ? primeiraLeituraPronta
        ? leitura.total
        : undefined
      : chaveContada === chaveDoRascunho
        ? contagemDaFolha
        : undefined;

  const baralhosFallback = useMemo(
    () => opcoesDeBaralho(agenda, forecast, leitura.decks),
    [agenda, forecast, leitura.decks],
  );
  // Facets carry every deck with at least one card, never windowed to the
  // forecast's seven days — unlike `baralhosFallback` (design.md Decisão 6,
  // spec scenario "Um baralho que só vence muito depois continua listado").
  const baralhos = useMemo(
    () => (facets ? facets.decks : baralhosFallback),
    [facets, baralhosFallback],
  );
  const fichas = useMemo(() => {
    const diaFicha = fichaDoDiaFixado(diaFixado, agenda);
    const outras = fichasDaSelecao(selecao, materias);
    return diaFicha ? [diaFicha, ...outras] : outras;
  }, [agenda, diaFixado, materias, selecao]);

  const podeLimpar =
    subjects.length > 0 ||
    keywords.length > 0 ||
    decks.length > 0 ||
    ahead ||
    limit !== DEFAULT_SESSION_LIMIT ||
    queueOrder !== "due" ||
    diaFixado !== null;

  const contagens = contagensDoTrilho({
    diaFixado: diaFixado !== null,
    materiasSelecionadas: subjects.length,
    materiasTotal: materias.length,
    palavrasSelecionadas: keywords.length,
    palavrasTotal: palavras.length,
    baralhosSelecionados: decks.length,
    baralhosTotal: baralhos.length,
    ahead,
    limit,
    queueOrder,
  });

  const onRemoverFicha = (ficha: FichaDeFiltro) => {
    if (ficha.dimension === DIA_FICHA_DIMENSION) {
      setDiaFixado(null);
      return;
    }
    remove(ficha.dimension, ficha.value);
  };

  const onLimpar = () => {
    clearAll();
    setDiaFixado(null);
  };

  const onIniciar = () => {
    if (!primeiraLeituraPronta || leitura.total === 0) return;
    navigate("/revisao/estudar", { state: { ...entregaMostrada.current, agenda } });
  };

  const onPraticarDia = (iso: string) => {
    const dia = agenda.find((item) => item.iso === iso);
    if (!dia || dia.decks.length === 0) return;
    navigate("/revisao/estudar", {
      state: { ...entregaPraticarDia(diaDoIso(iso), dia.decks), agenda },
    });
  };

  const secoes: RevisaoHomeSecoesProps = {
    agenda,
    diaFixado,
    onFixarDia: (iso) => setDiaFixado(iso),
    onPraticarDia,
    materias,
    materiasSelecionadas: subjects,
    onToggleMateria: (id) => toggle("subjects", id),
    palavras,
    palavrasSelecionadas: keywords,
    onTogglePalavra: (value) => toggle("keywords", value),
    baralhos,
    baralhosSelecionados: decks,
    onToggleBaralho: (name) => toggle("decks", name),
    ahead,
    onAhead: setAhead,
    limit,
    onLimit: setLimit,
    queueOrder,
    onQueueOrder: setQueueOrder,
  };

  const folha: RevisaoFilterSheetProps = {
    open: folhaAberta,
    onClose: () => setFolhaAberta(false),
    aba,
    onAba: setAba,
    materias: materiasDaFolha(materias),
    materiasSelecionadas: rascunho.subjects,
    onToggleMateria: (id) =>
      setRascunho((prev) => ({ ...prev, subjects: alternar(prev.subjects, id) })),
    palavras,
    palavrasSelecionadas: rascunho.keywords,
    onTogglePalavra: (value) =>
      setRascunho((prev) => ({ ...prev, keywords: alternar(prev.keywords, value) })),
    baralhos,
    baralhosSelecionados: rascunho.decks,
    onToggleBaralho: (name) =>
      setRascunho((prev) => ({ ...prev, decks: alternar(prev.decks, name) })),
    ahead: rascunho.ahead,
    onAhead: (value) => setRascunho((prev) => ({ ...prev, ahead: value })),
    limit: rascunho.limit,
    onLimit: (value) => setRascunho((prev) => ({ ...prev, limit: value })),
    queueOrder: rascunho.queueOrder,
    onQueueOrder: (value) => setRascunho((prev) => ({ ...prev, queueOrder: value })),
    resultCount: contagemDoBotao,
    onApply: () => {
      aplicarRascunho(selecao, rascunho, { toggle, setAhead, setLimit, setQueueOrder });
      setFolhaAberta(false);
    },
    onClear: () => setRascunho(selecaoVazia()),
    ...overridesDoDiaFixado(agenda, diaFixado),
  };

  const abrirFolha = () => {
    setRascunho(copiarSelecao(selecao));
    setAba("materia");
    setFolhaAberta(true);
  };

  return (
    <RevisaoPagina
      leitura={leitura}
      contagens={contagens}
      activeId={activeId}
      onSelect={(id) => setActiveId(id)}
      onIniciar={onIniciar}
      aguardando={!primeiraLeituraPronta}
      fichas={fichas}
      onRemoverFicha={onRemoverFicha}
      onLimpar={onLimpar}
      podeLimpar={podeLimpar}
      ahead={ahead}
      onAhead={setAhead}
      secoes={secoes}
      folha={folha}
      onAbrirFolha={abrirFolha}
      reservaDaBarraDeAbas
    />
  );
}

export interface RevisaoViewProps {
  /** Storybook passes this so the composition renders without the API. */
  dados?: RevisaoPaginaProps;
}

export default function RevisaoView({ dados }: RevisaoViewProps = {}) {
  if (dados) return <RevisaoPagina {...dados} />;
  return (
    <DefaultLayout hideFilter>
      <RevisaoAoVivo />
    </DefaultLayout>
  );
}
